import React, { useRef, useState } from "react";
import Spinner from "../Spinner";
import { getOffersByVariant } from "./offerCatalog";
import useCheckoutSession from "./useCheckoutSession";

const pricingNuance =
  "In het persoonlijke traject richten we een programma in op basis van jouw doel, startniveau, de fysieke eisen van jouw richting en het verschil dat we daartussen moeten overbruggen. Dit traject is geschikt voor verschillende startpunten en doelen: van politie, brandweer en KMar tot Defensie en specialistische eenheden zoals het Korps Commandotroepen. Kandidaten richting Defensie krijgen daarnaast toegang tot het militaire handboek. Twijfel je over je startniveau, blessuregeschiedenis of belastbaarheid? Dan is persoonlijke begeleiding verstandiger dan zelfstandig starten, of neem eerst contact op.";

const gridClassByCount = {
  1: "md:grid-cols-1 md:max-w-[420px]",
  2: "md:grid-cols-2 md:max-w-4xl",
  3: "md:grid-cols-3 md:max-w-6xl",
};

const consentMessage =
  "Ga eerst akkoord met de voorwaarden voordat je doorgaat naar betalen.";

const OfferCards = ({
  variant = "all",
  context = "pricing",
  showNuance = variant === "all",
}) => {
  const offers = getOffersByVariant(variant);
  const mobileScrollRef = useRef(null);
  const consentRef = useRef(null);
  const consentInputRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasConsent, setHasConsent] = useState(false);
  const [consentError, setConsentError] = useState("");
  const { startCheckout, loading, loadingProductKey, error } =
    useCheckoutSession();
  const isEmbedded = context === "embedded";
  const isSingleEmbeddedOffer = isEmbedded && offers.length === 1;

  const handleMobileScroll = () => {
    const el = mobileScrollRef.current;
    if (!el) return;
    const firstItem = el.firstElementChild;
    if (!firstItem) return;
    const gap = parseFloat(window.getComputedStyle(el).columnGap) || 0;
    const itemWidth = firstItem.getBoundingClientRect().width + gap;
    if (itemWidth === 0) return;
    const nextIndex = Math.round(el.scrollLeft / itemWidth);
    setActiveIndex(Math.max(0, Math.min(offers.length - 1, nextIndex)));
  };

  const handleConsentChange = (event) => {
    setHasConsent(event.target.checked);
    if (event.target.checked) {
      setConsentError("");
    }
  };

  const showConsentReminder = () => {
    setConsentError(consentMessage);
    consentRef.current?.scrollIntoView({
      behavior: "smooth",
      block: isEmbedded ? "center" : "nearest",
    });
    window.setTimeout(() => {
      consentInputRef.current?.focus();
    }, 250);
  };

  const handleCheckout = (productKey) => {
    if (!hasConsent) {
      showConsentReminder();
      return;
    }

    setConsentError("");
    startCheckout(productKey);
  };

  const consentBlock = (
    <div
      ref={consentRef}
      className={`w-full rounded-lg border border-mmBorder ${
        isEmbedded
          ? "mt-7 max-w-3xl bg-mmSurface/70 px-4 py-3 md:mt-8 md:px-4"
          : "mb-7 max-w-4xl bg-mmSurface px-4 py-4 md:px-5"
      }`}
    >
      <label
        className={`flex cursor-pointer items-start gap-3 leading-relaxed text-mmTextMuted ${
          isEmbedded ? "text-xs md:text-sm" : "text-sm"
        }`}
      >
        <input
          ref={consentInputRef}
          type="checkbox"
          checked={hasConsent}
          onChange={handleConsentChange}
          className="mt-1 h-4 w-4 shrink-0 accent-mmAccent"
        />
        <span>
          Ik ga akkoord met de{" "}
          <a
            href="/legal#terms-conditions"
            className="text-mmAccent hover:opacity-80"
          >
            voorwaarden
          </a>
          ,{" "}
          <a href="/legal#privacy" className="text-mmAccent hover:opacity-80">
            privacy-informatie
          </a>{" "}
          en{" "}
          <a
            href="/legal#checkout-conditions"
            className="text-mmAccent hover:opacity-80"
          >
            checkoutvoorwaarden
          </a>{" "}
          van Mission Movement. Ik begrijp dat digitale inhoud, toegang of
          voorbereiding direct na aankoop kan starten en dat mijn
          herroepingsrecht voor digitale inhoud daardoor kan vervallen.
        </span>
      </label>
      {consentError && <p className="mt-3 text-sm text-red-300">{consentError}</p>}
    </div>
  );

  return (
    <div
      className={`relative flex w-full flex-col items-center px-6 ${
        isEmbedded
          ? "bg-transparent pb-4 pt-7 md:pb-5 md:pt-8 lg:pt-8"
          : "bg-mmPage pb-16 pt-6 md:pb-14 md:pt-10 lg:pt-12"
      }`}
    >
      {loading && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center">
          <Spinner color="border-mmAccent" />
        </div>
      )}

      {error && (
        <p className="mb-5 max-w-2xl rounded-xl border border-red-500/40 bg-red-950/20 px-4 py-3 text-center text-sm text-red-300">
          {error}
        </p>
      )}

      {!isEmbedded && consentBlock}

      {/* Desktop/Tablet */}
      <div
        className={`hidden w-full justify-items-center gap-6 md:grid lg:gap-8 ${
          isEmbedded ? "items-start" : "items-stretch"
        } ${gridClassByCount[offers.length] || gridClassByCount[3]} mx-auto`}
      >
        {offers.map((offer) => (
          <OfferCard
            key={offer.productKey}
            data={offer}
            onCheckout={handleCheckout}
            loading={loadingProductKey === offer.productKey}
            disabled={loading || !hasConsent}
            onMissingConsent={showConsentReminder}
          />
        ))}
      </div>

      {showNuance && (
        <p className="hidden md:block max-w-5xl mt-24 rounded-xl border border-mmBorder bg-mmSurface px-5 py-3 text-center text-sm leading-relaxed text-mmTextMuted lg:mt-28">
          {pricingNuance}
        </p>
      )}

      {/* Mobile */}
      {isSingleEmbeddedOffer ? (
        <div className="w-full max-w-[360px] md:hidden">
          <OfferCard
            data={offers[0]}
            onCheckout={handleCheckout}
            highlighted
            loading={loadingProductKey === offers[0].productKey}
            disabled={loading || !hasConsent}
            onMissingConsent={showConsentReminder}
          />
        </div>
      ) : (
        <div className="md:hidden w-screen max-w-none -mx-6">
          {offers.length > 1 && (
            <p className="text-center text-xs uppercase tracking-widest text-mmTextMuted mb-3">
              Swipe om opties te vergelijken
            </p>
          )}
          <div
            ref={mobileScrollRef}
            onScroll={handleMobileScroll}
            className="flex w-full gap-4 overflow-x-auto snap-x snap-mandatory px-[7vw] pb-5 scroll-smooth overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {offers.map((offer, index) => (
              <div
                key={offer.productKey}
                className={`shrink-0 basis-[84vw] max-w-[360px] flex justify-center snap-center transition-all duration-300 ${
                  index === activeIndex
                    ? "scale-100 opacity-100"
                    : "scale-[0.96] opacity-80"
                }`}
              >
                <OfferCard
                  data={offer}
                  onCheckout={handleCheckout}
                  highlighted={index === activeIndex}
                  muted={index !== activeIndex}
                  loading={loadingProductKey === offer.productKey}
                  disabled={loading || !hasConsent}
                  onMissingConsent={showConsentReminder}
                />
              </div>
            ))}
          </div>
          {offers.length > 1 && (
            <div className="flex justify-center mt-4 space-x-2">
              {offers.map((offer, i) => (
                <span
                  key={offer.productKey}
                  className={`w-2.5 h-2.5 rounded-full border border-mmBorder transition-all ${
                    i === activeIndex
                      ? "bg-mmAccent border-mmAccent"
                      : "bg-transparent"
                  }`}
                />
              ))}
            </div>
          )}
          {showNuance && (
            <p className="mx-[7vw] mt-12 rounded-xl border border-mmBorder bg-mmSurface px-4 py-4 text-center text-sm leading-relaxed text-mmTextMuted">
              {pricingNuance}
            </p>
          )}
        </div>
      )}

      {isEmbedded && consentBlock}
    </div>
  );
};

const OfferCard = ({
  data,
  highlighted,
  muted,
  onCheckout,
  loading,
  disabled,
  onMissingConsent,
}) => {
  const {
    title,
    badge,
    description,
    priceLabel,
    features,
    excludedFeatures,
    productKey,
    buttonLabel,
    visualVariant,
  } = data;
  const isSelfGuided = visualVariant === "self";
  const shieldClipPath =
    "polygon(0 0, 100% 0, 100% calc(100% - var(--shield-point)), 50% 100%, 0 calc(100% - var(--shield-point)))";
  const buttonClass = isSelfGuided
    ? "border-mmAccent/70 bg-mmSurface text-mmAccent shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)] hover:border-mmAccent hover:bg-mmAccent/10 hover:text-mmAccent focus-visible:shadow-[0_0_0_2px_rgba(255,214,10,0.45)]"
    : "border-mmAccent bg-[linear-gradient(180deg,#ffe45c_0%,#ffd60a_55%,#d8ad00_100%)] text-black shadow-[0_10px_26px_rgba(255,214,10,0.16)] hover:border-mmAccent hover:bg-[linear-gradient(180deg,#fff070_0%,#ffd60a_50%,#cfa600_100%)] hover:shadow-[0_12px_30px_rgba(255,214,10,0.24)] focus-visible:shadow-[0_0_0_2px_rgba(255,214,10,0.45),0_10px_26px_rgba(255,214,10,0.18)]";
  const featureItems = [...features, ...excludedFeatures];

  return (
    <div
      style={{ clipPath: shieldClipPath }}
      className={`[--shield-point:30px] md:[--shield-point:38px] w-full max-w-[360px] h-full bg-mmBorder p-px shadow-sm transition-all duration-300 md:min-h-[468px] hover:bg-mmAccent/60 ${
        highlighted
          ? "bg-white/20 ring-1 ring-white/10"
          : muted
            ? "opacity-90"
            : ""
      }`}
    >
      <div
        style={{ clipPath: shieldClipPath }}
        className="flex h-full min-h-[438px] flex-col bg-mmSurface px-5 pb-11 pt-5 md:min-h-[468px] md:px-5 md:pb-12 md:pt-5"
      >
        <p className="mb-3 w-fit border border-mmAccent/40 bg-mmAccent/10 px-2.5 py-1 font-display text-[12px] uppercase tracking-[0.16em] text-mmAccent">
          {badge}
        </p>
        <h3
          className={`text-lg md:text-[19px] font-display uppercase tracking-widest leading-tight ${
            highlighted ? "text-mmAccent" : "text-mmText"
          }`}
        >
          {title}
        </h3>
        <p className="text-mmTextMuted mt-2.5 mb-3.5 text-sm leading-relaxed">
          {description}
        </p>
        <ul className="text-mmTextMuted text-sm space-y-1.5 mb-3">
          {featureItems.map((feature) => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>
        <p className="text-mmText font-semibold text-lg mt-auto mb-3.5">
          {priceLabel}
        </p>
        <div className="relative">
          <button
            className={`inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border px-5 py-2.5 text-center text-sm font-bold leading-tight transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClass}`}
            onClick={() => onCheckout(productKey)}
            disabled={disabled}
          >
            {loading ? "Betaling starten..." : buttonLabel}
          </button>
          {disabled && !loading && (
            <button
              type="button"
              aria-label="Ga eerst akkoord met de voorwaarden voordat je doorgaat naar betalen."
              className="absolute inset-0 cursor-not-allowed rounded-lg"
              onClick={onMissingConsent}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferCards;
